import React, { BaseSyntheticEvent, useEffect } from 'react'
import { IonButton, IonButtons, IonChip, IonIcon, IonLabel, IonRow, IonToolbar } from '@ionic/react'
import { chevronForward, closeCircleOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getPatientAllergiesSelector } from '../../selectors'
import { deletePatientAllergy, saveAllergies } from '../../store/patientAllergies.actions'
import MultiSelectButtons from '../../../../components/multiSelectButtons/MultiSelectButtons.component'
import './AllergiesButtons.component.css'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { isTablet } from '../../../../utilities'
import { setSelectedOption } from '../../../patientInformation/store/patientInformation.actions'
import { PatientInformationConstants } from '../../../patientInformation/store/patientInformation.constants'
import { IAllergyOptions } from '../../store/patientAllergies.types'
import { getPatientAllergyOptionsSelector } from '../../../options/selectors'
import { setChunkedPatientAllergiesOptions } from '../../../options/store/options.actions'
import { initializeSearch, setCurrentOptions } from '../../../../components/search/store/search.actions'
import { getSearchStateSelector } from '../../../../components/search/selectors'
import { getDisabledAllergyList } from '../../util/getDisabledAllergylist.util'

const AllergiesButtons: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const patientAllergiesState = useSelector(getPatientAllergiesSelector)
  const patientAllergyOptions = useSelector(getPatientAllergyOptionsSelector)

  const { allergies } = patientAllergiesState
  const search = useSelector(getSearchStateSelector)
  const { currentOptions } = search

  const {
    patientAllergies,
    medicationsAllergiesOptions,
    foodEnvironmentalAllergiesOptions,
    chunkedPatientAllergyOptions,
  } = patientAllergyOptions

  const onSelectAllergies = (e: BaseSyntheticEvent, options: IAllergyOptions) => {
    e.preventDefault()
    dispatch(saveAllergies(options))
  }

  useEffect(() => {
    dispatch(setCurrentOptions([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const moreButton = () => {
    dispatch(initializeSearch(patientAllergies))
    const initialChunkedData = patientAllergies.slice(0, 20)
    dispatch(setCurrentOptions(initialChunkedData))
    if (!currentOptions.length && !chunkedPatientAllergyOptions.length) {
      dispatch(setChunkedPatientAllergiesOptions(initialChunkedData))
    }

    if (isTablet()) {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_ALLERGIES_SEARCH_LIST_OPTION))
    } else {
      history.push(TabConstants.PATIENT_ALLERGIES_LIST)
    }
  }

  const deleteAllergy = (e: BaseSyntheticEvent, option: IAllergyOptions) => {
    e.stopPropagation()
    option.selected = false
    dispatch(deletePatientAllergy(option))
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let content: HTMLIonContentElement | null = document.querySelector('#allergies-button-row')
      content?.scrollBy(content?.scrollWidth, 0)
      content = null
    }, 10)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [allergies])

  return (
    <>
      <IonToolbar className='toolbar-padding'>
        <IonLabel className='toolbar-label' slot='start'>
          {t('patientAllergies.PatientAllergies')}:
        </IonLabel>
      </IonToolbar>
      {allergies && allergies.length > 0 ? (
        <IonToolbar>
          <IonRow class='ion-nowrap no-flex-shrink' id='allergies-button-row'>
            {allergies &&
              allergies.map((option: IAllergyOptions, index: number) => (
                <IonChip data-testid={`Chip_${option.name}`} key={index} className='ion-chip'>
                  <IonLabel>{option.name}</IonLabel>
                  <IonIcon color='white' icon={closeCircleOutline} onClick={(e) => deleteAllergy(e, option)} />
                </IonChip>
              ))}
          </IonRow>
        </IonToolbar>
      ) : (
        <IonRow>
          <IonToolbar color='light' className='labelHeader'>
            <IonLabel color='medium'>{t('patientAllergies.AllergiesLabel')}</IonLabel>
          </IonToolbar>
        </IonRow>
      )}
      <IonToolbar className='toolbar-padding'>
        <IonLabel className='toolbar-label' slot='start'>
          {t('patientAllergies.CommonAllergies')}
        </IonLabel>
        <IonButtons slot='end'>
          <IonButton className='toolbar-button' onClick={moreButton} color='primary-alt'>
            {t('common.buttons.FindMore')}
            <IonIcon icon={chevronForward} />
          </IonButton>
        </IonButtons>
      </IonToolbar>
      <hr />
      <MultiSelectButtons
        label={t('patientAllergies.Medications')}
        dataTestId={'Medications'}
        options={medicationsAllergiesOptions}
        selectAction={onSelectAllergies}
        selectedOptions={allergies}
        buttonValidation={getDisabledAllergyList}
      />
      <MultiSelectButtons
        label={t('patientAllergies.Environmental/Food')}
        dataTestId={'EnvironmentalFood'}
        options={foodEnvironmentalAllergiesOptions}
        selectAction={onSelectAllergies}
        selectedOptions={allergies}
        buttonValidation={getDisabledAllergyList}
      />
    </>
  )
}

export default AllergiesButtons
